import { useState, useEffect } from 'react';
import { superAdminApi } from '../services/api';
import { SuperAdminNav } from './SuperAdminConsole';
import Chip from '../components/Chip';
import { useApp } from '../AppContext';
import { DateField, inputToApi, defaultDateRange } from './AdminViewClients';

const PAGE_SIZE = 10;

function pageRange(current, total) {
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(total, current + delta);
  const range = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
}

export default function AdminClientRegistrations({ onNavigate }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [{ fromVal: defFrom, toVal: defTo }] = useState(() => defaultDateRange(12));
  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);
  const [appliedRange, setAppliedRange] = useState({ from: defFrom, to: defTo });

  const { addToast } = useApp();

  function loadRegistrations(range = appliedRange) {
    setLoading(true);
    superAdminApi.getClientRegistrations(inputToApi(range.from), inputToApi(range.to))
      .then(data => setRegistrations(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => setRegistrations([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadRegistrations(); }, []); // eslint-disable-line

  function applyDateRange() {
    const range = { from: fromDate, to: toDate };
    setAppliedRange(range);
    setPage(1);
    setSelectedIds(new Set());
    loadRegistrations(range);
  }

  function clearDateRange() {
    const range = { from: '', to: '' };
    setFromDate('');
    setToDate('');
    setAppliedRange(range);
    setPage(1);
    setSelectedIds(new Set());
    loadRegistrations(range);
  }

  const q = search.trim().toLowerCase();
  const filtered = registrations.filter(r =>
    !q ||
    (r.companyname || '').toLowerCase().includes(q) ||
    (r.clientname || '').toLowerCase().includes(q) ||
    (r.email || '').toLowerCase().includes(q) ||
    (r.phone || '').toLowerCase().includes(q) ||
    (r.onboardingstatus || '').toLowerCase().includes(q)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showFrom = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(safePage * PAGE_SIZE, filtered.length);

  function goTo(p) { setPage(Math.max(1, Math.min(p, totalPages))); }

  const allOnPageSelected = pageItems.length > 0 && pageItems.every(r => selectedIds.has(r.clientregistrationid));

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage(checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageItems.forEach(r => {
        if (checked) next.add(r.clientregistrationid);
        else next.delete(r.clientregistrationid);
      });
      return next;
    });
  }

  async function handleDeleteSelected() {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const idsAttempted = Array.from(selectedIds);
      const byId = new Map(registrations.map(r => [r.clientregistrationid, r]));
      const res = await superAdminApi.deleteClientRegistrations(idsAttempted);
      const results = Array.isArray(res) ? res : (res?.data || []);

      const withNames = results.map(r => ({
        ...r,
        companyName: byId.get(r.clientregistrationid)?.companyname || byId.get(r.clientregistrationid)?.clientname || `#${r.clientregistrationid}`,
      }));
      const failed = withNames.filter(r => !r.success);
      const succeeded = withNames.filter(r => r.success);

      if (failed.length === 0) {
        addToast(`Deleted ${succeeded.length} registration${succeeded.length !== 1 ? 's' : ''}.`, 'success');
      } else {
        addToast(`${succeeded.length} deleted, ${failed.length} failed.`, 'error');
      }

      setDeleteResults(withNames);
      // Keep only the failed ones selected so the user can see/retry them; drop succeeded ones.
      setSelectedIds(new Set(failed.map(r => r.clientregistrationid)));
      loadRegistrations();
    } catch (e) {
      addToast(e.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  }

  async function handleExportExcel() {
    setExporting(true);
    try {
      const blob = await superAdminApi.exportClientRegistrationsExcel(search.trim(), '',inputToApi(appliedRange.from), inputToApi(appliedRange.to));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ClientRegistrations.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addToast('Failed to export Excel. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }

  function closeDeleteModal() {
    setConfirmDelete(false);
    setDeleteResults(null);
  }

  return (
    <>
      <SuperAdminNav onNavigate={onNavigate} onChangePassword={() => {}} currentPage="admin-client-registrations" />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Registration List</div>
            <div className="psub">Everyone who's self-signed-up, verified or not, sourced from the registration queue</div>
          </div>
          <div className="flex g8">
            <button
              className="btn bout"
              style={{ opacity: exporting ? 0.6 : 1 }}
              disabled={exporting}
              onClick={handleExportExcel}
            >
              {exporting ? 'Exporting…' : '⬇ Export Excel'}
            </button>
          </div>
        </div>

        <div className="card mb16">
          <div className="fbar" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <div className="sw" style={{ flex: '1 1 220px', minWidth: 0 }}>
              <span className="sico">🔍</span>
              <input
                className="sinp"
                placeholder="Search by company, name, phone, email or status…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>From</label>
            <DateField value={fromDate} onChange={setFromDate} placeholder="From" />
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>To</label>
            <DateField value={toDate} onChange={setToDate} placeholder="To" />
            <button className="btn bpri bsm" onClick={applyDateRange}>Apply</button>
            <button className="btn bghost bsm" onClick={clearDateRange}>Clear</button>
            <button
              className="btn bsm"
              style={{ background: 'var(--rd)', color: '#fff', opacity: selectedIds.size === 0 ? 0.5 : 1 }}
              disabled={selectedIds.size === 0}
              onClick={() => setConfirmDelete(true)}
            >
              🗑 Delete Selected {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="twrap">
            <table>
              <thead>
                <tr>
                  <th><input type="checkbox" checked={allOnPageSelected} onChange={e => toggleAllOnPage(e.target.checked)} /></th>
                  <th>Company Name</th>
                  <th>Client Number</th>
                  <th>Client Name</th>
                  <th>Phone</th>
                  <th>Created On</th>
                  <th>OnBoarding Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>
                )}
                {!loading && pageItems.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>No registrations found</td></tr>
                )}
                {!loading && pageItems.map((r, i) => (
                  <tr key={r.clientregistrationid ?? i}>
                    <td><input type="checkbox" checked={selectedIds.has(r.clientregistrationid)} onChange={() => toggleSelect(r.clientregistrationid)} /></td>
                    <td className="td1">{r.companyname || '—'}</td>
                    <td className="tdsm">{r.clientnumber || '—'}</td>
                    <td>
                      <div style={{ fontWeight: 700, color: 'var(--or-dk)' }}>{r.clientname || '—'}</div>
                      <div class="tdm">{r.email || '—'}</div>
                    </td>
                    <td className="tdsm">{r.phone || '—'}</td>
                    <td className="tdsm">{r.createdon || '—'}</td>
                    <td><Chip label={r.onboardingstatus || '—'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex jb aic mt16">
            <div className="ts tg">Showing {showFrom}–{showTo} of {filtered.length} registration{filtered.length !== 1 ? 's' : ''}</div>
            {totalPages > 1 && (
              <div className="pag">
                <button className="pgbtn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>
                {safePage > 3 && (
                  <>
                    <button className="pgbtn" onClick={() => goTo(1)}>1</button>
                    {safePage > 4 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                  </>
                )}
                {pageRange(safePage, totalPages).map(p => (
                  <button key={p} className={`pgbtn${p === safePage ? ' on' : ''}`} onClick={() => goTo(p)}>{p}</button>
                ))}
                {safePage < totalPages - 2 && (
                  <>
                    {safePage < totalPages - 3 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                    <button className="pgbtn" onClick={() => goTo(totalPages)}>{totalPages}</button>
                  </>
                )}
                <button className="pgbtn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && !deleteResults && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
          onClick={() => !deleting && setConfirmDelete(false)}>
          <div style={{ background: 'var(--wh)', borderRadius: 16, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 420, margin: 20, overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: 'rgba(229,62,62,.1)' }}>🗑</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>Delete {selectedIds.size} Registration{selectedIds.size !== 1 ? 's' : ''}?</div>
              </div>
              <button onClick={() => !deleting && setConfirmDelete(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--g400)', padding: 2, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: '16px 22px', fontSize: 12.5, color: 'var(--g600)', lineHeight: 1.65 }}>
              ⚠️ This removes the registration request. If any of the selected rows already have a client account created, <strong style={{ color: 'var(--nv)' }}>that client account and its login will be permanently deleted too</strong>. This action <strong style={{ color: 'var(--rd)' }}>cannot be undone</strong>.
            </div>
            <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--g100)', display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(false)} disabled={deleting} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Cancel</button>
              <button disabled={deleting} onClick={handleDeleteSelected}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 700, cursor: deleting ? 'default' : 'pointer', background: '#E53E3E', color: '#fff', opacity: deleting ? 0.7 : 1 }}>
                {deleting ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : '🗑 Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteResults && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(26,31,54,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
          onClick={closeDeleteModal}>
          <div style={{ background: 'var(--wh)', borderRadius: 16, boxShadow: '0 16px 48px rgba(26,31,54,.22)', width: '100%', maxWidth: 480, margin: 20, overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--g100)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nv)' }}>
                Delete Results — {deleteResults.filter(r => r.success).length} deleted, {deleteResults.filter(r => !r.success).length} failed
              </div>
              <button onClick={closeDeleteModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--g400)', padding: 2, lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', padding: '10px 22px' }}>
              {deleteResults.map(r => (
                <div key={r.clientregistrationid} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--g100)' }}>
                  <span style={{ fontSize: 15 }}>{r.success ? '✅' : '❌'}</span>
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--nv)' }}>{r.companyName}</div>
                    {!r.success && <div style={{ fontSize: 11.5, color: 'var(--rd)', marginTop: 2 }}>{r.message || 'Delete failed'}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 22px 18px', borderTop: '1px solid var(--g100)', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeDeleteModal} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--g200)', background: 'var(--wh)', fontFamily: 'var(--fn)', fontSize: 12.5, fontWeight: 600, color: 'var(--g600)', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}